import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

// Token refresh state to prevent recursive loops
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: string) => void
  reject: (reason: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })
  failedQueue = []
}

// Environment-based configuration
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:8001/api'
  }
  // Production fallback - assumes API is on same domain
}

export const getImageBaseURL = () => {
  if (import.meta.env.VITE_IMAGE_URL) {
    return import.meta.env.VITE_IMAGE_URL
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:8001'
  }
  return ''
}

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Development mode configuration
const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development'
const hasDevEndpoints = import.meta.env.VITE_HAS_DEV_ENDPOINTS === 'true' || isDevelopment

api.interceptors.request.use((config) => {
  const authStore = useAuthStore()

  // Route to dev endpoints ONLY for unauthenticated users when dev endpoints are available
  if (hasDevEndpoints && config.url && !authStore.token) {
    const devRoutes: Record<string, string> = {
      '/categories': '/dev/categories',
      '/products': '/dev/products',
      '/inventory/low-stock': '/dev/inventory/low-stock',
      '/dashboard/metrics': '/dev/dashboard/metrics',
      '/dashboard/inventory-status': '/dev/dashboard/inventory-status',
      '/dashboard/sales-trends': '/dev/dashboard/sales-trends',
      '/dashboard/top-products': '/dev/dashboard/top-products',
      '/dashboard/recent-orders': '/dev/dashboard/recent-orders',
      '/dashboard/performance-kpis': '/dev/dashboard/performance-kpis',
      '/dashboard/stock-movement-trends': '/dev/dashboard/stock-movement-trends'
    }

    if (devRoutes[config.url] && !config.url.startsWith('/auth')) {
      config.url = devRoutes[config.url]
    }
  }

  // Public store routes must never receive an auth token.
  // Pattern: /store/{slug}/... where slug is NOT "settings".
  // The authenticated vendor route GET /store/settings must still get the token.
  const isPublicStoreRoute = !!config.url?.match(/^\/store\/(?!settings(?:\/|$))[^/]+/)
  const isPublicMarketplaceRoute = !!config.url?.match(/^\/marketplace(?:\/|$)/)

  if (authStore.token && !isPublicStoreRoute && !isPublicMarketplaceRoute) {
    config.headers.Authorization = `Bearer ${authStore.token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (typeof error.config & { skipSubscriptionRedirect?: boolean; _retry?: boolean })
    const authStore = useAuthStore()
    const responseData = error.response?.data ?? {}
    const responseCode = responseData?.code
    const subscriptionRequired = responseData?.subscription_required === true
      || responseCode === 'SUBSCRIPTION_REQUIRED'
      || responseCode === 'SUBSCRIPTION_EXPIRED'

    // Handle 402 Payment Required — subscription needed
    if (error.response?.status === 402 && subscriptionRequired && !originalRequest?.skipSubscriptionRedirect) {
      try {
        if (router.currentRoute.value.path !== '/subscription') {
          router.push('/subscription')
        }
      } catch (e) {
        const currentPath = window.location.pathname
        if (currentPath !== '/subscription' && currentPath !== '/login') {
          window.location.href = '/subscription'
        }
      }
      return Promise.reject(error)
    }

    // Handle 401 Unauthorized — token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Public store endpoints are unauthenticated — never attempt refresh or logout
      if (originalRequest.url?.match(/^\/store\//) || originalRequest.url?.match(/^\/marketplace(?:\/|$)/)) {
        return Promise.reject(error)
      }

      // Prevent refresh loop for auth endpoints
      if (originalRequest.url?.includes('/auth/')) {
        await authStore.handleUnauthorized()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await axios.post(`${getBaseURL()}/auth/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })

        const { token: newToken } = response.data
        authStore.token = newToken
        localStorage.setItem('auth_token', newToken)

        processQueue(null, newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        await authStore.handleUnauthorized()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
